<script setup lang="ts">
import { ref } from 'vue'
import { del, get, post } from '@/api'

definePage({
  meta: {
    title: 'S3 파일 리스트',
    action: 'read',
    subject: 'Utility',
  },
})

// S3 파일 인터페이스
interface S3File {
  name: string
  url: string
  lastModified: string  // ISO8601 형식 (서버에서 제공)
  selected?: boolean
}

// 상태
const s3List = ref<S3File[]>([])
const loading = ref(false)
const uploading = ref(false)
const deleting = ref(false)
const fileInput = ref<File[]>([])

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 테이블 헤더
const headers = [
  { title: '#', key: 'index', sortable: false, width: '60px' },
  { title: '파일 이름', key: 'name', sortable: true },
  { title: '파일 URL', key: 'url', sortable: false },
  { title: '등록 날짜', key: 'lastModified', sortable: true, width: '180px' },
  { title: '선택', key: 'selected', sortable: false, width: '80px' },
]

const urlStaticText = 'https://static.seoul.ventures/'

// 날짜 컬럼 정렬: 문자열이 아닌 실제 날짜 기준 비교
const customKeySort = {
  lastModified: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
}

// 날짜 포맷 (ISO8601 → 한국 로케일)
const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// 파일명 디코딩
const decodeName = (name: string) => {
  return name.split('/').map(decodeURIComponent).join('/')
}

// 전체 URL 생성
const getFullUrl = (name: string) => {
  return urlStaticText + decodeName(name)
}

// S3 파일 목록 로드
const loadFiles = async () => {
  loading.value = true
  try {
    const { success, data } = await get<S3File[]>('/utility')
    if (success && data)
      s3List.value = data.map(file => ({ ...file, selected: false }))
  }
  catch (error) {
    console.error('Failed to load files:', error)
    showNotification('파일 목록을 불러오는데 실패했습니다.', 'error')
  }
  finally {
    loading.value = false
  }
}

// 파일 업로드
const uploadFiles = async () => {
  if (!fileInput.value || fileInput.value.length === 0) {
    showNotification('파일을 선택해주세요.', 'warning')

    return
  }

  uploading.value = true
  try {
    const formData = new FormData()

    fileInput.value.forEach(file => {
      formData.append('files[]', file)
    })

    const { success } = await post('/utility', formData)

    if (success) {
      showNotification('파일이 업로드되었습니다.')
      fileInput.value = []
      await loadFiles()
    }
    else {
      showNotification('업로드에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Upload failed:', error)
    showNotification('업로드에 실패했습니다.', 'error')
  }
  finally {
    uploading.value = false
  }
}

// 선택된 파일 삭제
const deleteSelected = async () => {
  const selectedFiles = s3List.value.filter(file => file.selected)

  if (selectedFiles.length === 0) {
    showNotification('삭제할 파일을 선택해주세요.', 'warning')

    return
  }

  if (!confirm(`선택한 파일 ${selectedFiles.length}개를 삭제하시겠습니까?`))
    return

  deleting.value = true
  try {
    let successCount = 0
    for (const file of selectedFiles) {
      const fileName = decodeURIComponent(file.name)
      const { success } = await del(`/utility/deleteItems?=${fileName}`)
      if (success)
        successCount++
    }

    if (successCount > 0) {
      showNotification(`${successCount}개의 파일이 삭제되었습니다.`)
      await loadFiles()
    }
  }
  catch (error) {
    console.error('Delete failed:', error)
    showNotification('삭제에 실패했습니다.', 'error')
  }
  finally {
    deleting.value = false
  }
}

// URL 복사
const copyUrl = async (name: string) => {
  const url = getFullUrl(name)
  try {
    await navigator.clipboard.writeText(url)
    showNotification('URL이 복사되었습니다.')
  }
  catch {
    // 폴백: 구형 방식
    const input = document.createElement('input')

    input.value = url
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    showNotification('URL이 복사되었습니다.')
  }
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 초기 로드
onMounted(() => {
  loadFiles()
})
</script>

<template>
  <VRow>
    <!-- S3 파일 리스트 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>S3 파일 리스트</span>
          <VBtn
            icon="tabler-refresh"
            variant="text"
            :loading="loading"
            @click="loadFiles"
          />
        </VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headers"
            :items="s3List"
            :loading="loading"
            density="compact"
            :items-per-page="20"
            :custom-key-sort="customKeySort"
          >
            <template #item.index="{ index }">
              {{ index + 1 }}
            </template>

            <template #item.name="{ item }">
              <span class="text-break">
                {{ decodeName(item.name) }}
              </span>
            </template>

            <template #item.url="{ item }">
              <div class="d-flex align-center gap-2">
                <VTextField
                  :model-value="getFullUrl(item.name)"
                  density="compact"
                  hide-details
                  readonly
                  variant="outlined"
                  class="flex-grow-1"
                  style="max-width: 400px;"
                />
                <VBtn
                  size="small"
                  variant="outlined"
                  @click="copyUrl(item.name)"
                >
                  URL 복사
                </VBtn>
              </div>
            </template>

            <template #item.lastModified="{ item }">
              {{ formatDate(item.lastModified) }}
            </template>

            <template #item.selected="{ item }">
              <VCheckbox
                v-model="item.selected"
                hide-details
                density="compact"
              />
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 파일 업로드 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>파일 업로드</VCardTitle>
        <VCardText>
          <VFileInput
            v-model="fileInput"
            label="파일 선택"
            placeholder="파일을 선택하거나 드래그하세요"
            multiple
            show-size
            prepend-icon="tabler-upload"
            :disabled="uploading"
          />

          <div class="d-flex gap-2 mt-4">
            <VBtn
              color="primary"
              :loading="uploading"
              :disabled="!fileInput || fileInput.length === 0"
              @click="uploadFiles"
            >
              <VIcon
                icon="tabler-upload"
                class="me-1"
              />
              파일 업로드
            </VBtn>
            <VBtn
              color="error"
              :loading="deleting"
              variant="outlined"
              @click="deleteSelected"
            >
              <VIcon
                icon="tabler-trash"
                class="me-1"
              />
              선택 삭제
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.text-break {
  word-break: break-all;
}
</style>
